import {
  hexToOklch,
  isValidHex,
  normalizeHex,
  normalizeHue,
  oklchToHex,
  oklchToLinearRgb,
  rgbToHex,
} from '@/lib/color-formats';

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** Reduce chroma at constant OKLCH lightness and hue until the color fits sRGB. */
export function fitOklch(l: number, c: number, h: number) {
  l = clamp(l, 0, 1);
  c = Math.max(0, c);
  h = normalizeHue(h);
  const inGamut = (chroma: number) =>
    Object.values(oklchToLinearRgb(l, chroma, h)).every((v) => v >= -0.000001 && v <= 1.000001);
  if (inGamut(c)) return { l, c, h, hex: oklchToHex(l, c, h), mapped: false };
  let low = 0;
  let high = c;
  for (let i = 0; i < 24; i += 1) {
    const mid = (low + high) / 2;
    if (inGamut(mid)) low = mid;
    else high = mid;
  }
  return { l, c: low, h, hex: oklchToHex(l, low, h), mapped: true };
}

const numeric = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;
const channel = (value: string, percentScale = 1): number | null => {
  const percent = value.endsWith('%');
  const number = percent ? value.slice(0, -1) : value;
  if (!numeric.test(number)) return null;
  const parsed = Number(number) * (percent ? percentScale / 100 : 1);
  return Number.isFinite(parsed) ? parsed : null;
};
const hue = (value: string): number | null => {
  if (value.includes('%')) return null;
  const match = /^(.+?)(deg|turn|rad|grad)?$/.exec(value);
  if (!match) return null;
  const n = channel(match[1]!);
  if (n === null) return null;
  return normalizeHue(
    n * ({ turn: 360, rad: 180 / Math.PI, grad: 0.9, deg: 1 }[match[2] ?? 'deg'] ?? 1),
  );
};

/** Opaque authored colors only. A rejected value never silently becomes black. */
export function parseColorInput(text: string): { hex: string; mapped: boolean } | null {
  const value = text.trim().toLowerCase();
  if (isValidHex(value)) return { hex: normalizeHex(value), mapped: false };
  const match = /^(rgba?|hsla?|oklch|oklab)\(([^()]+)\)$/.exec(value);
  if (!match) return null;
  const format = match[1] === 'rgba' ? 'rgb' : match[1] === 'hsla' ? 'hsl' : match[1];
  const [body, alpha, extra] = match[2]!.split('/');
  if (extra !== undefined || (alpha !== undefined && channel(alpha.trim()) !== 1)) return null;
  const legacy = body!.includes(',');
  if (legacy && (!['rgb', 'hsl'].includes(format!) || alpha !== undefined)) return null;
  const parts = legacy ? body!.split(',').map((p) => p.trim()) : body!.trim().split(/\s+/);
  if (legacy && parts.length === 4 && channel(parts[3]!) === 1) parts.pop();
  if (parts.length !== 3) return null;
  const [x, y, z] = parts as [string, string, string];
  if (format === 'rgb') {
    const values = parts.map((p) => channel(p, 255));
    if (values.some((v) => v === null)) return null;
    return { hex: rgbToHex(values[0]!, values[1]!, values[2]!), mapped: false };
  }
  if (format === 'hsl') {
    const h = hue(x);
    const s = channel(y);
    const l = channel(z);
    if (h === null || s === null || l === null || !y.endsWith('%') || !z.endsWith('%')) return null;
    const sat = clamp(s, 0, 1);
    const light = clamp(l, 0, 1);
    const a = sat * Math.min(light, 1 - light);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      return 255 * (light - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)));
    };
    return { hex: rgbToHex(f(0), f(8), f(4)), mapped: false };
  }
  const l = channel(x);
  const second = channel(y, 0.4);
  const third = format === 'oklch' ? hue(z) : channel(z, 0.4);
  if (l === null || second === null || third === null) return null;
  const c = format === 'oklch' ? Math.max(0, second) : Math.hypot(second, third);
  const h = format === 'oklch' ? third : (Math.atan2(third, second) * 180) / Math.PI;
  return fitOklch(l, c, h);
}

export function colorDistance(a: string, b: string): number {
  const x = hexToOklch(a);
  const y = hexToOklch(b);
  const axis = (c: typeof x) => [
    c.c * Math.cos((c.h * Math.PI) / 180),
    c.c * Math.sin((c.h * Math.PI) / 180),
  ];
  const [xa, xb] = axis(x);
  const [ya, yb] = axis(y);
  return Math.hypot(x.l - y.l, xa! - ya!, xb! - yb!);
}
